import React, { useEffect, useId, useRef, useState } from "react";
import { useGoogleAds } from "./utils/use-google-ads.ts";
import { requestAdDisplay } from "./contexts/ad-config-context.tsx";
import { cn } from "../utils/cn.ts";

interface GoogleAdProps {
  adSlotName: string;
  className?: string;
}

// Fetch when the ad container comes within two viewport heights of the screen.
const FETCH_ROOT_MARGIN = "200% 0px";

export const GoogleAd: React.FC<GoogleAdProps> = ({ adSlotName, className }) => {
  const adContainerId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const { getSlot, networkCode } = useGoogleAds();
  const adSlot = getSlot(adSlotName);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!networkCode || !adSlot) return;

    const gtag = (window.googletag = window.googletag ?? ({ cmd: [] } as unknown as typeof googletag));
    let slot: googletag.Slot | null = null;
    const onRenderEnded = (event: googletag.events.SlotRenderEndedEvent) => {
      if (slot && event.slot === slot) setIsLoaded(true);
    };
    gtag.cmd.push(() => {
      const mapping = adSlot.mapping
        .reduce((builder, entry) => builder.addSize(entry[0], entry[1]), gtag.sizeMapping())
        .build();
      slot = gtag.defineSlot(`${networkCode}${adSlot.adSlotId}`, [300, 250], adContainerId);
      if (slot) {
        if (mapping) slot.defineSizeMapping(mapping);
        slot.setConfig({ targeting: { pos: adSlotName } });
        slot.addService(gtag.pubads());
        gtag.pubads().addEventListener("slotRenderEnded", onRenderEnded);
      }
    });

    // Fetch only when the ad is about to enter the viewport. Containers hidden
    // at the current breakpoint (display:none) never intersect, so each
    // instance fetches only once it is both laid out and near the screen.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          requestAdDisplay({ getSlot: () => slot, containerId: adContainerId });
        }
      },
      { rootMargin: FETCH_ROOT_MARGIN },
    );
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      gtag.cmd.push(() => {
        gtag.pubads().removeEventListener("slotRenderEnded", onRenderEnded);
      });
    };
  }, []);

  if (!networkCode || !adSlot) {
    console.error(`GoogleAd: ad config missing for slot "${adSlotName}"`);
    return null;
  }

  return (
    <div
      id={adContainerId}
      ref={containerRef}
      className={cn("w-[300px] h-[250px]", !isLoaded && "animate-pulse bg-neutral-50", className)}
    ></div>
  );
};
